'use client'

import { IconChevronDown } from '@cleanlab/design-system/icons'
import { cn } from '@cleanlab/design-system/utils'
import { Accordion as AccordionPrimitive } from 'radix-ui'
import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ComponentRef,
  forwardRef,
} from 'react'

// Sidebar-specific Accordion with safe focus ring that does not overflow horizontally

type SideNavAccordionProps = Omit<
  ComponentProps<typeof AccordionPrimitive.Root>,
  'type' | 'collapsible' | 'value' | 'defaultValue' | 'onValueChange'
> & {
  value?: string[]
  defaultValue?: string[]
  onValueChange?: (value: string[]) => void
}

const SideNavAccordion = ({ className, ...props }: SideNavAccordionProps) => (
  <AccordionPrimitive.Root
    {...props}
    type="multiple"
    className={cn('flex w-full flex-col pb-0', className)}
  />
)

const SideNavAccordionItem = forwardRef<
  ComponentRef<typeof AccordionPrimitive.Item>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={className} {...props} />
))
SideNavAccordionItem.displayName = 'SideNavAccordionItem'

const SideNavAccordionTrigger = forwardRef<
  ComponentRef<typeof AccordionPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        'group/sidebar-accordion-trigger type-body-100 relative flex flex-1 items-center justify-between border-t border-t-border-1 px-7 py-5 text-start text-text-primary outline-none outline-offset-0 ring-0 ring-offset-0 transition-colors focus:shadow-none focus:outline-none focus-visible:!outline-none focus-visible:outline-offset-0 focus-visible:!ring-0 focus-visible:ring-offset-0 data-[disabled]:cursor-not-allowed data-[disabled]:text-text-disabled [&>*]:text-text-primary [&[data-state=open]>[data-icon]]:rotate-180',
        className
      )}
      {...props}
    >
      {/* Focus ring contained within bounds to avoid horizontal clipping */}
      <div className="pointer-events-none absolute inset-0 opacity-0 ring-2 ring-inset ring-focus transition-none group-focus-visible/sidebar-accordion-trigger:opacity-100" />
      {children}
      <IconChevronDown
        size={16}
        data-icon
        className="size-4 shrink-0 text-text-disabled transition-transform duration-200 group-data-[disabled]/sidebar-accordion-trigger:text-text-disabled/70"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
))
SideNavAccordionTrigger.displayName = 'SideNavAccordionTrigger'

const SideNavAccordionContent = forwardRef<
  ComponentRef<typeof AccordionPrimitive.Content>,
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className={cn(
      'overflow-hidden pb-3 pt-2 data-[state=closed]:animate-collapsible-close data-[state=open]:animate-collapsible-open',
      className
    )}
    {...props}
  >
    <div className="px-7 py-0">{children}</div>
  </AccordionPrimitive.Content>
))
SideNavAccordionContent.displayName = 'SideNavAccordionContent'

export { SideNavAccordion, SideNavAccordionContent, SideNavAccordionItem, SideNavAccordionTrigger }
